/**
 * Converts a notification entity type identifier to a human-readable label.
 *
 * This utility function takes a string representing the entity type
 * associated with a notification and returns a user-friendly display label
 * suitable for UI elements. If the entityType does not match any known
 * types, it returns 'General' as a fallback.
 *
 * @param entityType - The type of the entity that generated the notification (e.g., 'policy', 'changeManagementProcess')
 * @returns A human-readable label corresponding to the notification entity type
 */
export const notificationEntityLabel = (entityType: string): string => {
  switch (entityType) {
    case 'changeManagementProcess':
      return 'Change Management Process'
    case 'policy':
      return 'Policy'
    case 'task':
      return 'Task'
    case 'dashboard':
      return 'Dashboard'
    case 'meetingMinute':
      return 'Meeting Minutes'
    case 'credentialRegistry':
      return 'Credential Registry'
    case 'exemptionsRegister':
      return 'Exemptions Register'
    case 'incomingQuestionnaire':
      return 'Incoming Questionnaire'
    case 'serviceProvider':
      return 'Supply Chain Management'
    case 'serviceProviderAssessment':
      return 'Supply Chain Assessment'
    default:
      return 'General'
  }
}

export const socketNotificationEntityLabel = (entityType: string): string => {
  switch (entityType) {
    case 'incident':
      return 'Incident'
    case 'department':
      return 'Department'
    case 'external-integration':
      return 'External Integration'
    case 'file-repository':
      return 'Document Repository'
    case 'role-default-permission':
      return 'Role Default Permission'
    case 'staff':
      return 'Staff'
    case 'user':
      return 'User'
    case 'change-management-process-category':
      return 'Change Management Process Category'
    case 'change-management-process':
      return 'Change Management Process'
    case 'non-conformance':
      return 'Non-Conformance'
    case 'task':
      return 'Task'
    case 'ofi':
      return 'Opportunity for Improvement'
    case 'assessment-detail':
      return 'Assessment Detail'
    case 'agent-run':
      return 'AI Remediation'
    case 'assessment':
      return 'Assessment'
    case 'audit':
      return 'Audit'
    case 'soa':
      return 'Statement of Applicability'
    case 'control':
      return 'Control'
    case 'country-regulatory-register-oos':
      return 'Country Regulatory Register'
    case 'jurisdiction':
      return 'Jurisdiction'
    case 'operation-type':
      return 'Operation Type'
    case 'region':
      return 'Region'
    case 'regulatory-category':
      return 'Regulatory Category'
    case 'risk-type':
      return 'Risk Type'
    case 'exemptions-evidence':
      return 'Exemptions Evidence'
    case 'exemption-register':
      return 'Exemption Register'
    case 'asset-type':
      return 'Asset Type'
    case 'information-asset':
      return 'Information Asset'
    case 'jira-integration':
      return 'Jira Integration'
    case 'meeting-minute-type':
      return 'Meeting Minute Type'
    case 'organisation-module':
      return 'Organisation Module'
    case 'action-status':
      return 'Action Status'
    case 'affected-area':
      return 'Affected Area'
    case 'importance':
      return 'Importance'
    case 'root-identification':
      return 'Root Identification'
    case 'policy-category':
      return 'Policy Category'
    case 'policy-review-frequency':
      return 'Policy Update Frequency'
    case 'policy':
      return 'Policy'
    case 'project':
      return 'Project'
    case 'incident-type':
      return 'Incident Type'
    case 'risk-category':
      return 'Risk Category'
    case 'risk-identification-source':
      return 'Risk Identification Source'
    case 'risk-impact':
      return 'Risk Impact'
    case 'risk-likelihood':
      return 'Risk Likelihood'
    case 'risk-owner':
      return 'Risk Owner'
    case 'risk-rating':
      return 'Risk Rating'
    case 'risk':
      return 'Risk'
    case 'threat-actor':
      return 'Threat Actor'
    case 'threat-category':
      return 'Threat Category'
    case 'threat-source':
      return 'Threat Source'
    case 'threat':
      return 'Threat'
    case 'vulnerability':
      return 'Vulnerability'
    case 'assessment-tier-criticality':
      return 'Assessment Tier Criticality'
    case 'assessment-tier-mapping':
      return 'Assessment Tier Mapping'
    case 'assessment-tier-sensitivity':
      return 'Assessment Tier Sensitivity'
    case 'assessment-tier':
      return 'Assessment Tier'
    case 'service-provider-contract-status':
      return 'Supply Chain Contract Status'
    case 'service-provider':
      return 'Supply Chain Management'
    case 'service-type':
      return 'Service Type'
    case 'service-provider-assessment':
      return 'Supply Chain Assessment'
    case 'assessment-template-questionnaire-question':
      return 'Assessment Template Questionnaire Question'
    case 'assessment-template-questionnaire':
      return 'Questionnaire Library'
    case 'assessment-template':
      return 'Assessment Template'
    case 'organisation-standard':
      return 'Organisation Standard'
    case 'task-severity':
      return 'Task Severity'
    case 'task-status':
      return 'Task Status'
    default:
      // Fallback: convert kebab-case/snake_case to Title Case
      return (
        entityType
          ?.replaceAll('-', ' ')
          .replaceAll('_', ' ')
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ') || 'Entity'
      )
  }
}
